// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 20-agent-plugins.md#marketplace-repo-bootstrap
// @greenlight-spec: decisions/0040-multiple-plugin-marketplace-repositories.md

import * as React from 'react';
import { LockKeyhole } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DOCS_LINKS } from '@/lib/docsLinks';
import { cn } from '@/lib/utils';
import type {
  AgentKind,
  PluginMarketplaceStatus,
  PluginMarketplaceVisibility,
} from '@greenlight/shared';

import { MarketplaceRepositoryTable } from './MarketplaceRepositoryTable';
import { UnregisterMarketplaceDialog } from './UnregisterMarketplaceDialog';
import {
  type BootstrapMarketplace,
  type UnregisterMarketplace,
  useMarketplaceEditor,
} from './useMarketplaceEditor';

export interface MarketplaceEditorProps {
  repositories: PluginMarketplaceStatus[];
  githubAppReady: boolean;
  onBootstrap: BootstrapMarketplace;
  /** Omitted by the install wizard step — unregistering is a Settings-only capability. */
  onUnregister?: UnregisterMarketplace;
  /** SCM org handle shown as a fixed prefix on the repository name field. */
  orgHandle?: string | null;
  /**
   * Where this editor is mounted — the stale-installation recovery message
   * points at "Start over" on the wizard's GitHub App step (mid-wizard only,
   * per docs/07 §Re-install semantics) or at Settings' "Re-create App" action
   * (the only recovery once install is complete), never both.
   */
  recoveryContext: 'wizard' | 'settings';
  /**
   * Set to `outline` where creating a repository is no longer the surface's
   * primary action — the install wizard demotes it once a repository exists and
   * Continue takes over, so only one filled button is on screen.
   */
  createVariant?: 'default' | 'outline';
  /** Scroll to the create form on mount and focus its name field (a direct link to create). */
  focusCreate?: boolean;
  /** Supported agents whose organization rollout clones from GitHub; a private pick warns about them. */
  directCloneAgents?: readonly AgentKind[];
  className?: string;
}

const DIRECT_CLONE_LABELS: Record<AgentKind, string> = {
  claude: 'Claude Code CLI',
  codex: 'Codex',
  cursor: 'Cursor',
  copilot: 'Copilot',
  other: 'other agents',
};

/** "Copilot clones", "Copilot and other agents clone". */
function directCloneSentence(agents: readonly AgentKind[]): string {
  const labels = agents.map((agent) => DIRECT_CLONE_LABELS[agent]);
  const list =
    labels.length <= 1
      ? labels.join('')
      : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  const singular = agents.length === 1 && agents[0] !== 'other';
  return `${list.charAt(0).toUpperCase()}${list.slice(1)} ${singular ? 'clones' : 'clone'}`;
}

export function MarketplaceEditor({
  repositories,
  githubAppReady,
  onBootstrap,
  onUnregister,
  orgHandle = null,
  recoveryContext,
  createVariant = 'default',
  focusCreate = false,
  directCloneAgents = [],
  className,
}: MarketplaceEditorProps): React.JSX.Element {
  const createFormRef = React.useRef<HTMLDivElement>(null);
  const repoNameRef = React.useRef<HTMLInputElement>(null);
  const {
    visibility,
    setVisibility,
    repoName,
    setRepoName,
    repoNameInvalid,
    canCreate,
    pendingKey,
    error,
    errorCode,
    create,
    reconcile,
    unregisterTarget,
    requestUnregister,
    cancelUnregister,
    unregisterPending,
    unregisterError,
    confirmUnregister,
  } = useMarketplaceEditor(onBootstrap, onUnregister);

  const createEnabled = githubAppReady && canCreate;
  const formBusy = pendingKey !== null || !githubAppReady;

  React.useEffect(() => {
    if (!focusCreate) return;
    createFormRef.current?.scrollIntoView?.({ block: 'center' });
    repoNameRef.current?.focus({ preventScroll: true });
  }, [focusCreate]);

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {!githubAppReady && (
        <div
          className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground"
          data-testid="marketplace-blocked"
        >
          Waiting for the GitHub App connection before repositories can be created.
        </div>
      )}

      {repositories.length > 0 ? (
        <MarketplaceRepositoryTable
          repositories={repositories}
          pendingKey={pendingKey}
          onReconcile={reconcile}
          onUnregister={onUnregister ? requestUnregister : undefined}
        />
      ) : (
        <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
          No marketplace repositories have been created yet.
        </div>
      )}

      <div
        ref={createFormRef}
        className="rounded-md border bg-muted/20 p-4"
        data-testid="marketplace-create-form"
      >
        <h3 className="text-sm font-semibold">Create another repository</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Start with a private repository: Claude, Codex and Cursor each sync it through their own
          GitHub connection, so members need no GitHub access. A public repository is the fallback
          for Copilot, self-serve installs, and Claude Code without a claude.ai sign-in, which clone
          it on each machine, or for when OpenAI or Cursor cannot sync the private one.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-[9rem_minmax(0,1fr)_auto] sm:items-start">
          <div className="space-y-2">
            <Label htmlFor="marketplace-visibility">Visibility</Label>
            <Select
              value={visibility}
              onValueChange={(value) => setVisibility(value as PluginMarketplaceVisibility)}
              disabled={formBusy}
            >
              <SelectTrigger id="marketplace-visibility" aria-label="Repository visibility">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="private">Private</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="marketplace-repo-name">Repository name</Label>
            <div
              className={cn(
                'flex h-9 w-full overflow-hidden rounded-md border border-input bg-transparent shadow-sm',
                'focus-within:ring-1 focus-within:ring-ring',
                formBusy && 'opacity-50',
              )}
              data-testid="marketplace-repo-field"
            >
              {orgHandle ? (
                <span
                  className="flex shrink-0 items-center border-r border-input bg-muted/40 px-3 font-mono text-sm text-muted-foreground"
                  data-testid="marketplace-repo-org-prefix"
                >
                  {orgHandle}/
                </span>
              ) : (
                <span
                  className="flex shrink-0 items-center border-r border-input bg-muted/40 px-3 font-mono text-sm text-muted-foreground"
                  data-testid="marketplace-repo-org-prefix-placeholder"
                >
                  org/
                </span>
              )}
              <Input
                ref={repoNameRef}
                id="marketplace-repo-name"
                value={repoName}
                maxLength={100}
                onChange={(event) => setRepoName(event.target.value)}
                disabled={formBusy}
                aria-invalid={repoNameInvalid}
                aria-describedby={
                  !githubAppReady
                    ? 'marketplace-create-waiting'
                    : repoNameInvalid
                      ? 'marketplace-repo-name-error'
                      : undefined
                }
                className="h-full border-0 shadow-none focus-visible:ring-0"
                data-testid="marketplace-repo-input"
              />
            </div>
            {repoNameInvalid && (
              <p id="marketplace-repo-name-error" className="text-xs text-destructive">
                Use only letters, numbers, &ldquo;.&rdquo;, &ldquo;-&rdquo;, and &ldquo;_&rdquo;.
              </p>
            )}
          </div>
          <Button
            type="button"
            variant={createVariant}
            onClick={() => void create()}
            disabled={!createEnabled}
            className="h-11 sm:mt-7 sm:h-9"
            data-testid="marketplace-create"
          >
            {pendingKey === 'create'
              ? 'Creating…'
              : githubAppReady
                ? 'Create repository'
                : 'Waiting for GitHub…'}
          </Button>
        </div>
        {!githubAppReady && (
          <p
            id="marketplace-create-waiting"
            className="mt-2 text-xs text-muted-foreground"
            data-testid="marketplace-create-waiting"
          >
            Create stays disabled until the GitHub App installation is connected.
          </p>
        )}

        {visibility === 'private' && directCloneAgents.length > 0 && (
          <Alert className="mt-3" data-testid="marketplace-private-direct-clone">
            <LockKeyhole aria-hidden="true" />
            <AlertDescription>
              <p>
                {directCloneSentence(directCloneAgents)} the marketplace from GitHub.{' '}
                {directCloneAgents.length === 1 ? 'It needs' : 'They need'} a public repository, or
                every user needs read access to{' '}
                <span className="font-mono">
                  {orgHandle ?? 'org'}/{repoName.trim() || 'repository'}
                </span>
                .{' '}
                <a
                  href={DOCS_LINKS.buildWithYourAgent}
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-4"
                >
                  Setup docs
                </a>
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 h-11 md:h-8"
                onClick={() => setVisibility('public')}
                disabled={formBusy}
              >
                Use Public instead
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {visibility === 'private' && directCloneAgents.length === 0 && (
          <Alert className="mt-3">
            <LockKeyhole aria-hidden="true" />
            <AlertDescription>
              Claude, Codex and Cursor sync a private repository through their own GitHub App and
              serve members a copy, so members need no GitHub access. Self-serve installs clone it,
              so they work only on machines with Git access to this repository.
            </AlertDescription>
          </Alert>
        )}
        {error && errorCode === 'plugin_marketplace.scm_installation_invalid' ? (
          <Alert
            variant="destructive"
            className="mt-3"
            data-testid="marketplace-installation-invalid"
          >
            <AlertDescription>
              {recoveryContext === 'wizard' ? (
                <>
                  The connected GitHub App installation is stale and Greenlight can no longer act
                  through it. Go back to the <strong>GitHub App</strong> step above, choose{' '}
                  <strong>Start over</strong>, then reinstall the App into your organization.
                </>
              ) : (
                <>
                  The connected GitHub App installation is stale and Greenlight can no longer act
                  through it. Go to <strong>Source control</strong> above and choose{' '}
                  <strong>Re-create App</strong> to reconnect it.
                </>
              )}
            </AlertDescription>
          </Alert>
        ) : (
          error && (
            <Alert variant="destructive" className="mt-3" data-testid="marketplace-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )
        )}
      </div>

      <UnregisterMarketplaceDialog
        repository={unregisterTarget}
        pending={unregisterPending}
        error={unregisterError}
        onConfirm={confirmUnregister}
        onCancel={cancelUnregister}
      />
    </div>
  );
}
